import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigation} from '@react-navigation/native';
import {get, noop} from 'lodash';

import Button from '/component/base/Button';
import Icon from '/component/base/Icon';
import BasePopover from '/component/base/Popover';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import {useMyTeamsContext} from '/component/screen/report/MyTeamsReport/hooks/useMyTeamsContext';
import {UserProfileModal} from '/constant/TestIDs';
import {
  MY_TEAMS,
  REPORT_VOLUME_PERFORMANCE,
  REPORT_WATCH_LIST,
} from '/enum/records.enum';
import {COACH_PROFILE} from '/enum/recordType.enum';
import {REPORT_CARD_ELIGIBLE_ACCELERATOR_BONUS} from '/enum/reportCard.enum';
import {
  ACTION_TYPES,
  EVENT_NAMES,
  SCREEN_NAMES,
  USER_TYPES,
  WATCHLIST_ACTION,
} from '/enum/tealium.enum';
import {withSafeArea} from '/provider/SafeAreaProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {
  addCoachToWatchlist,
  removeCoachFromWatchlist,
  setState,
} from '/redux/action/myTeams.action';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {downlineCoachIds as watchlistSelector} from '/redux/selector/watchList.selector';
import {generateCallback} from '/util/component.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {withHOC} from '/util/hoc.util';
import {hasAchievedHighestRank} from '/util/rank.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import {ALL_CONTENT} from './CoachReportPopover.constant';
import {styles} from './CoachReportPopover.style';
import {getPopoverContent, getStyles} from './CoachReportPopover.util';
import {CoachInfo, Volume} from './component';
interface CoachReportPopoverProps {
  tt: (key: string, fallback: string) => string;
  ts: (key: string, fallback: string) => string;
  safeAreaInsets: Record<string, number>;
  popoverClose: () => void;
  reportRecordData: Record<string, any>;
  contentName: string;
  contentProps: Record<string, any>;
  onViewProfilePress: () => void;
  userId: string;
  lastPaidRank: string;
  highestAchievedRank: string;
  coachCurrentRank: string;
  downlineCoachIds: number[];
  recordsName: string;
}

export const CoachReportPopover: React.FC<CoachReportPopoverProps> = ({
  tt = (key, fallback) => fallback,
  ts = (key, fallback) => fallback,
  safeAreaInsets = {},
  popoverClose = noop,
  reportRecordData = null,
  contentName = '',
  contentProps = {},
  onViewProfilePress = noop,
  userId = '',
  lastPaidRank = '',
  recordsName = '',
  highestAchievedRank = '',
  coachCurrentRank = '',
  downlineCoachIds = [],
}) => {
  const {width, height} = useWindowDimensions();
  const isLandscape = width > height;
  const isWeb = Platform.OS === 'web';
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const currentWatchlistIds = useSelector(watchlistSelector);
  const featureFlag = useSelector(variablesFeatureFlag);
  const showV2Orders = shouldReleaseFeature(
    get(featureFlag, 'ff_v2_orders', ''),
  );
  const [loading, setLoading] = useState(false);
  const {reduxActiveTab, reduxIsViewTeam, reduxScreenContext} =
    useMyTeamsContext();

  const screenContext = reduxIsViewTeam
    ? SCREEN_NAMES.coachTeamsReport
    : reduxScreenContext;
  const {
    id,
    firstName,
    lastName,
    rank: {id: rankId} = {},
  } = reportRecordData || {};
  const {popoverStyle, footerButtonStyle} = getStyles(
    safeAreaInsets,
    isLandscape,
  );
  const PopoverContent = getPopoverContent(contentName, ALL_CONTENT);
  const hasNextRank = !hasAchievedHighestRank(rankId);
  const disablePreviewScroll =
    contentName === REPORT_CARD_ELIGIBLE_ACCELERATOR_BONUS;

  const sendEventprofileUpdate =
    getTealiumUtilDefaultInstance().generateTrackEvent({
      eventName: EVENT_NAMES.profileUpdate,
    });
  const [isInWatchlist, setIsInWatchlist] = useState(
    id ? downlineCoachIds.map(String).includes(String(id)) : false,
  );

  useEffect(() => {
    setIsInWatchlist(
      id ? downlineCoachIds.map(String).includes(String(id)) : false,
    );
  }, [id, downlineCoachIds]);

  const handleToggleWatchlist = async () => {
    if (!id || loading) return;
    setLoading(true);
    try {
      if (isInWatchlist) {
        await dispatch(
          removeCoachFromWatchlist({
            downlineCoachId: {downlineCoachId: String(id)},
          }),
        );

        const newIds = currentWatchlistIds.filter(
          (watchlistId: string | number) => String(watchlistId) !== String(id),
        );
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListIds',
            stateValue: {
              count: newIds.length,
              downlineCoachIds: newIds,
            },
          }),
        );
        let actionReport_name;

        if (reduxIsViewTeam) {
          actionReport_name = WATCHLIST_ACTION.viewTeam;
        } else {
          if (reduxActiveTab === 'my-teams') {
            actionReport_name = WATCHLIST_ACTION.myTeams;
          } else {
            actionReport_name = WATCHLIST_ACTION.watchList;
          }
        }

        setIsInWatchlist(false);
        sendEventprofileUpdate({
          updated_fields: 'watchlist',
          userId: String(id ?? ''),
          report_name: actionReport_name,
          action: ACTION_TYPES.remove,
          lastPaidRank: lastPaidRank,
          coach_highest_rank: highestAchievedRank,
          coachCurrentRank: coachCurrentRank,
          coach_id: String(userId ?? ''),
        });
      } else {
        await dispatch(
          addCoachToWatchlist({
            downlineCoachId: {downlineCoachId: String(id)},
          }),
        );
        const cleanIds = currentWatchlistIds.filter(
          (watchlistId: string | number) => String(watchlistId) !== String(id),
        );
        const newIds = [...cleanIds, String(id)];
        dispatch(
          setState({
            reportName: REPORT_WATCH_LIST,
            stateName: 'watchListIds',
            stateValue: {
              count: newIds.length,
              downlineCoachIds: newIds,
            },
          }),
        );
        setIsInWatchlist(true);
        let actionReport_name;
        if (reduxIsViewTeam) {
          actionReport_name = WATCHLIST_ACTION.viewTeam;
        } else {
          if (reduxActiveTab === 'my-teams') {
            actionReport_name = WATCHLIST_ACTION.myTeams;
          } else {
            actionReport_name = WATCHLIST_ACTION.watchList;
          }
        }

        sendEventprofileUpdate({
          updated_fields: 'watchlist',
          userId: String(id ?? ''),
          action: ACTION_TYPES.add,
          report_name: actionReport_name,
          lastPaidRank: lastPaidRank,
          coach_highest_rank: highestAchievedRank,
          coachCurrentRank: coachCurrentRank,
          coach_id: String(userId ?? ''),
        });
      }

      // Redux actions automatically update the state, no need for manual navigation params update
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  };

  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.profileClick,
  });

  const generateOnViewProfilePress = generateCallback({
    func: sendEvent,
    params: {
      userType: USER_TYPES.coach,
      userId: String(id),
      userFullName: `${firstName} ${lastName}`,
      lastPaidRank: lastPaidRank,
      screenName: screenContext,
      coachHighestRank: highestAchievedRank,
      coachCurrentRank: coachCurrentRank,
    },
  });

  const handleVolumePerformancePress = () => {
    popoverClose();
    // @ts-expect-error TS(2339): Property 'push' does not exist on type 'NavigationProp<ParamListBase, string, Readonly<{ key: string; index: number; routeNames: string[]; history?: unknown[]; routes: (Readonly<{ key: string; name: string; path?: string; }> & { ...; })[]; type: string; stale: false; }>, {}, {}>'.
    navigation.push('card', {
      modalName: REPORT_VOLUME_PERFORMANCE,
      modalProps: {
        VolPerfUserId: id,
        VolPerfUserName: `${firstName} ${lastName}`,
      },
    });
  };

  return (
    <BasePopover style={popoverStyle} padded={false} accessible={false}>
      <View style={styles.closeButton}>
        <TouchableOpacity
          {...testProperties(null, true)}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={ts('close', 'Close')}
          onPress={popoverClose}>
          <Icon name="close" />
        </TouchableOpacity>
      </View>

      <Gutter size={isLandscape ? 12 : 20}>
        <CoachInfo
          hasNextRank={hasNextRank}
          reportRecordData={reportRecordData}
        />
      </Gutter>

      {disablePreviewScroll ? (
        <View {...testProperties(null, true)} accessible={false}>
          <PopoverContent {...contentProps} recordData={reportRecordData} />
          <Gutter size={isLandscape ? 12 : 20}>
            <Volume reportRecordData={reportRecordData} />
            {(recordsName === MY_TEAMS || recordsName === REPORT_WATCH_LIST) &&
              showV2Orders && (
                <Button
                  type="secondary"
                  accessibilityLabel={`View volume performance for ${firstName} ${lastName}`}
                  onPress={handleVolumePerformancePress}
                  style={{
                    base: styles.volumePerformanceButton,
                    text: styles.volumePerformanceButtonText,
                  }}>
                  {tt('footer.volumePerformance.label', 'Volume Performance')}
                </Button>
              )}
          </Gutter>
        </View>
      ) : (
        <ScrollView {...testProperties(UserProfileModal.reportPopoverScroll)}>
          <View {...testProperties(null, true)}>
            <PopoverContent {...contentProps} recordData={reportRecordData} />
            <Gutter size={isLandscape ? 12 : 20}>
              <Volume reportRecordData={reportRecordData} />
              {(recordsName === MY_TEAMS ||
                recordsName === REPORT_WATCH_LIST) &&
                showV2Orders && (
                  <Button
                    type="secondary"
                    accessibilityLabel={`View volume performance for ${firstName} ${lastName}`}
                    onPress={handleVolumePerformancePress}
                    style={{
                      base: styles.volumePerformanceButton,
                      text: styles.volumePerformanceButtonText,
                    }}>
                    {tt('footer.volumePerformance.label', 'Volume Performance')}
                  </Button>
                )}
            </Gutter>
          </View>
        </ScrollView>
      )}

      <View
        style={[
          footerButtonStyle,
          {
            alignItems: 'center',
            justifyContent: 'center',
            paddingBottom: isLandscape ? 8 : 0,
          },
        ]}>
        <Gutter size={isLandscape ? 12 : 20}>
          {recordsName === MY_TEAMS && userId !== id ? (
            <View
              style={[
                styles.ButtonContainer,
                isLandscape && styles.ButtonContainerLandscape,
              ]}>
              <TouchableOpacity
                testID="toggleWatchlistButton"
                onPress={handleToggleWatchlist}
                disabled={loading}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel={
                  isInWatchlist ? 'On Watchlist' : 'Add to Watchlist'
                }
                style={[
                  styles.watchListButton,
                  isWeb && styles.watchListButtonWeb,
                  !isLandscape && styles.watchListButtonPortraitSpacing,
                  isInWatchlist
                    ? styles.onWatchlistButton
                    : styles.removeWatchListButton,
                ]}>
                {loading ? (
                  <ActivityIndicator
                    size="small"
                    color="black"
                    style={styles.starIcon}
                  />
                ) : (
                  <Icon
                    name={isInWatchlist ? 'star_Filed' : 'starUnfilled'}
                    style={[
                      styles.starIcon,
                      isInWatchlist ? styles.wishList : styles.UnwishList,
                    ]}
                    testID={isInWatchlist ? 'star_Filed' : 'starUnfilled'}
                  />
                )}

                <Text
                  numberOfLines={isWeb ? 1 : undefined}
                  style={[
                    isInWatchlist
                      ? styles.wishListButtonText
                      : styles.UnwishListButtonText,
                    isWeb && styles.watchListButtonTextWeb,
                  ]}
                  isBold>
                  {isInWatchlist
                    ? tt('footer.removeWatchlist.label', 'On Watchlist')
                    : tt('footer.addWatchlist.label', 'Add to Watchlist')}
                </Text>
              </TouchableOpacity>

              <Button
                testID={UserProfileModal.viewProfile}
                disabled={!id}
                accessibilityLabel={`View profile for ${firstName} ${lastName}`}
                onPress={() => {
                  popoverClose(
                    generateCallback({
                      func: onViewProfilePress,
                      params: {recordType: COACH_PROFILE, recordId: id},
                    }),
                    generateOnViewProfilePress(),
                  );
                }}
                style={{
                  base: styles.viewProfileButton,
                  ...(isWeb
                    ? {minHeight: 28, paddingHorizontal: 14, paddingVertical: 6}
                    : {}),
                  ...(isWeb ? {minWidth: 96, borderRadius: 3} : {}),
                  text: isWeb ? {fontSize: 13} : undefined,
                }}>
                {tt('footer.profileButton.label', 'View Profile')}
              </Button>
            </View>
          ) : (
            <Button
              testID={UserProfileModal.viewProfile}
              disabled={!id}
              accessibilityLabel={`View profile for ${firstName} ${lastName}`}
              onPress={() => {
                popoverClose(
                  generateCallback({
                    func: onViewProfilePress,
                    params: {recordType: COACH_PROFILE, recordId: id},
                  }),
                  generateOnViewProfilePress(),
                );
              }}
              style={{
                base: {
                  ...(isLandscape ? styles.singleViewProfileLandscape : {}),
                  ...(isWeb
                    ? {minHeight: 28, paddingHorizontal: 14, paddingVertical: 6}
                    : {}),
                  ...(isWeb ? {minWidth: 96} : {}),
                },
                text: isWeb ? {fontSize: 13} : undefined,
              }}>
              {tt('footer.profileButton.label', 'View Profile')}
            </Button>
          )}
        </Gutter>
      </View>
    </BasePopover>
  );
};

export default withHOC(
  withSafeArea,
  withTranslation('reportCard.coach'),
)(CoachReportPopover);
